import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import { userService } from '../services/userService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  // Flag set to true when the backend rejects the token as expired/invalid.
  // The Login page reads this to show a friendly "session expired" banner.
  const [sessionExpired, setSessionExpired] = useState(false);

  const isAuthenticated = !!token;

  // Clean-up helper — centralises state reset without setting sessionExpired
  const clearSession = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('timezone');
    localStorage.removeItem('localePreference');
  }, []);

  // Explicit user-initiated logout
  const logout = useCallback(() => {
    setSessionExpired(false);
    clearSession();
  }, [clearSession]);

  // Listen for the custom event dispatched by the shared apiClient interceptor
  // when the server returns 401 (expired / invalid token).
  useEffect(() => {
    const handleTokenExpired = () => {
      clearSession();
      setSessionExpired(true);
    };

    window.addEventListener('auth:token-expired', handleTokenExpired);
    return () => window.removeEventListener('auth:token-expired', handleTokenExpired);
  }, [clearSession]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const regStatus = await authService.getRegistrationStatus();
        setRegistrationEnabled(regStatus);

        if (token) {
          const username = localStorage.getItem('username');
          const timezone = localStorage.getItem('timezone') || 'UTC';
          const localePreference = localStorage.getItem('localePreference') || 'en-GB';
          if (username) {
            setUser({ username, timezone, localePreference });
          }
          try {
            const prefs = await userService.getPreferences();
            if (prefs) {
              localStorage.setItem('timezone', prefs.timezone || 'UTC');
              localStorage.setItem('localePreference', prefs.localePreference || 'en-GB');
              setUser(prev => prev ? { ...prev, timezone: prefs.timezone || 'UTC', localePreference: prefs.localePreference || 'en-GB' } : null);
            }
          } catch (e) {
            console.warn('Could not fetch latest user preferences:', e);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (username, password) => {
    try {
      const response = await authService.login(username, password);
      const { token: newToken, username: userUsername, timezone = 'UTC', localePreference = 'en-GB' } = response;

      setToken(newToken);
      setUser({ username: userUsername, timezone, localePreference });
      setSessionExpired(false);

      localStorage.setItem('token', newToken);
      localStorage.setItem('username', userUsername);
      localStorage.setItem('timezone', timezone);
      localStorage.setItem('localePreference', localePreference);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const register = async (username, password, email) => {
    try {
      const response = await authService.register(username, password, email);
      const { token: newToken, username: userUsername, timezone = 'UTC', localePreference = 'en-GB' } = response;

      setToken(newToken);
      setUser({ username: userUsername, timezone, localePreference });
      setSessionExpired(false);

      localStorage.setItem('token', newToken);
      localStorage.setItem('username', userUsername);
      localStorage.setItem('timezone', timezone);
      localStorage.setItem('localePreference', localePreference);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateUserPreferences = async (newTimezone, newLocalePreference) => {
    try {
      const res = await userService.updatePreferences(newTimezone, newLocalePreference);
      const updatedTz = res.timezone || newTimezone || 'UTC';
      const updatedLoc = res.localePreference || newLocalePreference || 'en-GB';
      localStorage.setItem('timezone', updatedTz);
      localStorage.setItem('localePreference', updatedLoc);
      setUser(prev => prev ? { ...prev, timezone: updatedTz, localePreference: updatedLoc } : null);
      return { success: true, data: res };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Dismiss the session-expired banner without logging out again
  const clearSessionExpired = () => setSessionExpired(false);

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    registrationEnabled,
    sessionExpired,
    login,
    register,
    logout,
    updateUserPreferences,
    clearSessionExpired,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};